import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { ResourceLinks } from "../../../../constants";
import { makeArrayRandom } from "../../../../utils/learner";
import { useSelector } from "../../../../store";

export default function WebCorrectOrderMatch({ question, onAnswer = () => {} }) {
    const questionData = question;
    const learnerState = useSelector((state) => state.learner || {});
    const data = learnerState?.details?.testLevel === "vfh" ? questionData?.vfh : questionData?.acsf4;
    const imagesResources = ResourceLinks.llnQuizImage;

    const [websiteChoices, setWebsiteChoices] = useState({});
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [activeSelect, setActiveSelect] = useState(false);
    const [selectedKeyPrevious, setSelectedKeyPrevious] = useState(null);

    const [initialChoicesCount, setInitialChoicesCount] = useState(Array.isArray(questionData.answer.value)
        ? questionData.answer.value.length
        : Object.keys(questionData.answer.value).length);

    const [availableChoices, setAvailableChoices] = useState(
        makeArrayRandom(data.choices)
    );

    // every time question change we will set to reset needed to reset
    useEffect(() => {
        setWebsiteChoices({});
        setSelectedChoice(null);
        setActiveSelect(false);
        setSelectedKeyPrevious(null);
        setInitialChoicesCount(Array.isArray(questionData.answer.value)
            ? questionData.answer.value.length
            : Object.keys(questionData.answer.value).length);
        setAvailableChoices(makeArrayRandom(data.choices));
    }, [question,
        questionData.answer.value,
        data.choices,
    ]);

    const handleChoiceClick = (choice) => {
        setSelectedChoice(choice);
        setActiveSelect(true);
        setSelectedKeyPrevious(null);
    };

    const handleChoiceAssign = (index) => {
        if (websiteChoices[index] && !selectedChoice) {
            const choice = data.choices.find((c) => c.value === websiteChoices[index].value);
            setSelectedChoice(choice);
            setSelectedKeyPrevious(index);
        }

        if (selectedChoice && (selectedChoice?.value !== websiteChoices[index]?.value)) {
            let currentWebsiteChoices = {...websiteChoices};
            const previousChoice = websiteChoices[index];

            if (previousChoice) {
                setAvailableChoices((prevChoices) => [
                    ...prevChoices,
                    { ...previousChoice },
                ]);
            }

            // we will check here if coming from previous answer
            if (selectedKeyPrevious !== null) {
                delete currentWebsiteChoices[selectedKeyPrevious];
                setSelectedKeyPrevious(null);
            }
            // updating answer
            currentWebsiteChoices = {
                ...currentWebsiteChoices,
                [index]: {
                    value: selectedChoice.value,
                },
            }
            setWebsiteChoices(currentWebsiteChoices);

            if (
                typeof questionData.answer.value === "object" &&
                !Array.isArray(questionData.answer.value)
            ) {
                const answerKeys = Object.keys(questionData.answer.value);

                const formattedAnswer = answerKeys.reduce(
                    (acc, key, keyIndex) => {
                        acc[key] = currentWebsiteChoices[keyIndex]?.value || null;
                        return acc;
                    },
                    {}
                );

                const canProceed = true;
                onAnswer({
                    canProceed,
                    answer: formattedAnswer,
                });
            } else {
                const updatedAnswer = Object.values(currentWebsiteChoices).map(
                    (choice) => choice.value
                );

                const canProceed = true;
                onAnswer({
                    canProceed,
                    answer: updatedAnswer,
                });
            }

            setAvailableChoices((prevChoices) =>
                prevChoices.filter(
                    (item) => item.value !== selectedChoice.value
                )
            );

            setSelectedChoice(null);
            setActiveSelect(false);
        }
    };

    const handleRemoveChoice = (index) => {
        if (!activeSelect) {
            const removedChoice = websiteChoices[index];

            if (removedChoice) {
                setWebsiteChoices((prevChoices) => {
                    const newChoices = { ...prevChoices };
                    delete newChoices[index];

                    if (
                        typeof questionData.answer.value === "object" &&
                        !Array.isArray(questionData.answer.value)
                    ) {
                        const answerKeys = Object.keys(
                            questionData.answer.value
                        );

                        let canProceed = false;
                        const formattedAnswer = answerKeys.reduce(
                            (acc, key, keyIndex) => {
                                acc[key] = newChoices[keyIndex]?.value || null;
                                if (acc[key] !== "" && acc[key] !== null) {
                                    canProceed = true;
                                }
                                return acc;
                            },
                            {}
                        );
                        onAnswer({
                            canProceed,
                            answer: formattedAnswer,
                        });
                    } else {
                        const updatedAnswer = Object.values(newChoices).map(
                            (choice) => choice.value
                        );

                        // set to false so that next button will be disabled
                        const canProceed = updatedAnswer.length > 0;
                        onAnswer({
                            canProceed,
                            answer: updatedAnswer,
                        });
                    }

                    return newChoices;
                });

                setAvailableChoices((prevChoices) => [
                    ...prevChoices,
                    {
                        value: removedChoice.value,
                    },
                ]);
            }
        }
    };

    return (
        <Box
            sx={{
                maxWidth: "1000px",
                margin: "0 auto",
            }}
        >
            <Box sx={{
                display: "flex",
                position: "relative",
                textAlign: "center"
            }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    width: "650px",
                    height: "440px",
                    padding: "10px",
                    backgroundColor: "rgb(122, 140, 142)",
                    borderBottom: "10px solid rgb(91, 105, 107)",
                    borderRadius: "10px",
                    textAlign: "center",
                }}>
                    {[...Array(initialChoicesCount)].map((_, index) => (
                        <Box key={index} sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                        }}>
                            <Box
                                component="img"
                                src={`${imagesResources}${data.images[index].image}`}
                                sx={{
                                    marginBottom: "10px"
                                }}
                            />
                            <Box sx={{
                                    height: "70px",
                                    width: "110px",
                                    backgroundColor: "white",
                                    position: "relative",
                                    display: "inline-block",
                                    boxSizing: "border-box",
                                    padding: "4px",
                                    lineHeight: "0.8em",
                                    textAlign: "center",
                                    boxShadow:
                                            selectedChoice
                                                ? "rgb(199, 0, 199) 0px 0px 0px 3px"
                                                : "none",
                                    borderTop: websiteChoices[data.images[index].index]
                                                ? "none"
                                                : "10px solid rgb(91, 105, 106)",
                                }}
                                onClick={() => handleChoiceAssign(data.images[index].index)}
                            >
                                {/* For close button */}
                                {websiteChoices[data.images[index].index] && (
                                    <Box
                                        className="selected-close-btn"
                                        sx={{
                                            top: "0px",
                                            right: "0px",
                                            fontWeight:
                                                typeof questionData.answer.value ===
                                                    "object" &&
                                                !Array.isArray(
                                                    questionData.answer.value
                                                )
                                                    ? "normal"
                                                    : "bold",
                                            width: "17.33px",
                                            maxHeight: "20px",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveChoice(data.images[index].index);
                                        }}
                                    >
                                        X
                                    </Box>
                                )}
                                {websiteChoices[data.images[index].index] ? (
                                            <Box
                                                style={{
                                                    position: "absolute",
                                                    top: "0",
                                                    bottom: "0",
                                                    left: "0",
                                                    right: "0",
                                                    lineHeight: "0.8em",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Box sx={{
                                                fontWeight: "bold",
                                                display: "inline-flex",
                                                width: "100%",
                                                height: "100%",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                userSelect: "none",
                                                pointerEvents: "none",
                                                fontSize: "16px",
                                                backgroundColor: "#aad6e7",
                                            }}>{websiteChoices[data.images[index].index].value}</Box>
                                            </Box>
                                        ) :
                                        <Box></Box>
                                    }
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Box sx={{
                    position: "relative",
                    width: "170px",
                    marginLeft: "10px",
                    border: "1px dashed lightgrey",
                    textAlign: "center"
                }}>
                    <Box sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-around",
                        alignItems: "center",
                        position: "absolute",
                        top: "0",
                        left: "0",
                        textAlign: "center",
                    }}>
                        {availableChoices.map((choice, index) => (
                            <Box key={index} sx={{
                                    borderBottom: "10px solid rgb(52, 148, 186)",
                                    position: "relative",
                                    height: "62px",
                                    width: "102px",
                                    display: "inline-block",
                                    boxSizing: "content-box",
                                    backgroundColor: "#aad6e7",
                                    padding: "4px",
                                    lineHeight: "0.8em",
                                    textAlign: "center",
                                    boxShadow:
                                    selectedChoice?.value === choice.value
                                            ? "rgb(199, 0, 199) 0px 0px 0px 3px"
                                            : "none",
                                }}
                                onClick={() => { handleChoiceClick(choice) }}
                                disabled={selectedChoice === choice}
                            >
                                <Box sx={{
                                    fontWeight: "bold",
                                    display: "inline-flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    userSelect: "none",
                                    pointerEvents: "none",
                                    fontSize: "16px",
                                }}>
                                    { choice.value }
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};